/**
 * All Candidates Entry Point.
 * Creates its own bundle.
 */
import { h } from 'preact';
import { MutableRef } from 'preact/hooks';
import {
	useState, useContext, useEffect, useReducer, useCallback, useRef, useMemo,
} from 'preact/compat';
import CustomSelect from './inputs/CustomSelect';
import { Theme } from '../lib/render';
import { deepEqual } from '../lib/helpers';
import Results from './Results';
import Loader from './Loader';
import { CandidateAPIResponse, WPConfig, BoxProps } from '../types';
import CandidateBox from './CandidateBox';

interface SearchQueryProps {
	CandidateCard?: (props: BoxProps) => h.JSX.Element;
}

/**
 * Posts per page.
 * @todo Make this configurable.
 * @warning This must match the posts_per_page value in the Candidate_Search_API.
 *
 * @type {number}
 */
const POSTS_PER_PAGE = window.EM_EA_DATA.posts_per_page;

/**
 * Search.
 */
export interface APIParams {
	state?: string;
	position?: string;
	race_priority?: string;
	election_cycle: string;
	pageID: string;
}

export enum SearchActionEnum {
	SET_STATE = 'SET_STATE',
	CLEAR_SEARCH = 'CLEAR_SEARCH',
}

export type SearchAction = {
	type: SearchActionEnum.SET_STATE,
	payload: {
		key: keyof APIParams,
		value: string,
	},
} | {
	type: SearchActionEnum.CLEAR_SEARCH,
};

const Filters = ({
	theme,
	dispatchSearch,
	searchParams,
	hasSearchParams,
}: {
	theme: WPConfig,
	dispatchSearch: (action: SearchAction) => void,
	searchParams: APIParams,
	hasSearchParams: number,
}) => {
	if (!Object.keys(theme.filters).length) {
		return null;
	}
	return (
		<div className="ea-filter-bar">
			<div className={`ea-filter-bar__filters count-${Object.keys(theme.filters).length}`}>
				{Object.keys(theme.filters).map((filter) => (
					<CustomSelect
						label={theme.filters[filter].label}
						options={[
							{ value: '', label: 'All' },
							...theme.filters[filter].options,
						]}
						value={searchParams[filter as keyof APIParams] ?? ''}
						onChange={(item) => {
							dispatchSearch({
								type: SearchActionEnum.SET_STATE,
								payload: {
									key: filter as keyof APIParams,
									value: item.value,
								},
							});
						}}
					/>
				))}
			</div>
			<div className="ea-filter-bar__clear">
				<button
					disabled={!hasSearchParams}
					className="ea-btn-reset"
					onClick={() => {
						dispatchSearch({
							type: SearchActionEnum.CLEAR_SEARCH,
						});
					}}
					type="button"
				>
					Clear Filters
				</button>
			</div>
		</div>
	);
};

/**
 * Provide default values,
 * ensure stable state object.
 */
const defaultParams: APIParams = {
	state: '',
	position: '',
	race_priority: '',
	election_cycle: '',
	pageID: '',
};

/**
 * Search Reducer.
 * Allows handling a generic search state object.
 */
const searchReducer = (state: APIParams, action: SearchAction) => {
	switch (action.type) {
	case 'SET_STATE':
		// eslint-disable-next-line no-case-declarations
		const { payload } = action;
		return {
			...defaultParams,
			...state,
			[payload.key]: payload.value,
		};
	case 'CLEAR_SEARCH':
		return {
			...defaultParams,
		};
	default:
		return state;
	}
};

const PostQuery = ({
	CandidateCard = CandidateBox,
}: SearchQueryProps) => {
	const theme = useContext(Theme);
	const [candidates, setCandidates] = useState(theme.candidates);
	const [max, setMax] = useState(0); // Max pages.
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const searchParamsRef = useRef(null);
	const pageRef = useRef(page);

	/**
	 * Set initial search params.
	 * This is a stable object.
	 */
	const startingParams = useMemo(() => {
		defaultParams.election_cycle = theme.election_cycle;
		defaultParams.pageID = theme.pageID;
		return defaultParams;
	}, [theme.election_cycle]);
	const [searchParams, dispatchSearch] = useReducer(searchReducer, startingParams);

	/**
	 * Fetch candidates from the API.
	 */
	const fetchCandidates = useCallback(async (
		params: APIParams,
		curPage: number,
		allowUpdate: boolean,
		init: MutableRef<boolean>,
	) => {
		const url = `${theme.rest_base}/search?${new URLSearchParams({
			...params,
			page: curPage.toString(),
		}).toString()}`;

		const data = await fetch(url);
		const json = await data.json() as CandidateAPIResponse;

		// Prevent race conditions.
		if (!allowUpdate) {
			return;
		}

		/**
		 * Init query, set:
		 * - page to 1
		 * - set max pages to queried value
		 */
		if (init.current) {
			setPage(1);
			setMax(json.max);
		} else if (
			deepEqual(params, searchParamsRef.current)
			&& curPage > pageRef.current
		) {
			/**
			 * Page increment query.
			 */
			setCandidates((c) => [...c, ...json.candidates]);
		} else {
			/**
			 * Params change query.
			 */
			setPage(1);
			setCandidates(() => json.candidates);
			setMax(json.max);
		}

		pageRef.current = curPage;
		searchParamsRef.current = params;
	}, [theme, pageRef, candidates, searchParamsRef]);

	/**
	 * Fetch candidates from API.
	 * Refetch when user selectes filters change.
	 * We utilize the prepopulated candidates from the theme object,
	 * we lazy fetch the page/maxPage on init.
	 * Otherwise we fetch the candidates on param change.
	 *
	 * isSubscribed — tracks race condition
	 */
	const init = useRef<boolean>(true);
	useEffect(() => {
		let isSubscribed = true;

		/**
		 * No loading on page queries. We assume a page query if page ref is less than current page.
		 * No loading on init (candidates already populated)
		 */
		if (!init.current && !(pageRef.current < page)) {
			setLoading(true);
		}

		fetchCandidates(searchParams, page, isSubscribed, init)
			.catch((e) => {
				// eslint-disable-next-line no-console
				console.error(e);
				setError(true);
			})
			.finally(() => {
				setLoading(false);
				init.current = false;
			});
		return () => {
			isSubscribed = false;
		};
	}, [searchParams, page, pageRef]);

	/**
	 * Has search params.
	 * Exclude election_cycle since always present.
	 */
	const { pageID, election_cycle, ...filteredParams } = searchParams;
	const hasSearchParams = Object.values(filteredParams).filter((v) => v !== '').length;

	return (
		<div className="ea-index">
			<Filters
				theme={theme}
				dispatchSearch={dispatchSearch}
				searchParams={searchParams}
				hasSearchParams={hasSearchParams}
			/>
			{loading ? (
				<Loader />
			) : (
				<Results
					candidates={candidates}
					page={page}
					pageRef={pageRef}
					max={max}
					setPage={setPage}
					ajax
					postPerPage={POSTS_PER_PAGE}
					CandidateCard={CandidateCard}
				/>
			)}
			{error && (
				<div className="ea-form-error">
					<p>There was an error fetching candidates. Please refresh.</p>
				</div>
			)}
		</div>
	);
};

export default PostQuery;
